import { useEffect, useState } from "react";
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from "@headlessui/react";
import { Modal, Button } from "@common";

// NetSuite names come through as "Bank Balance : Bank Balance : RBC … - [1234]".
// Drop the repeated "Bank Balance :" prefix so the picker is readable.
function cleanBankName(name) {
  if (!name) return "";
  return name.replace(/^(?:\s*Bank Balance\s*:\s*)+/i, "").trim();
}

const EMPTY_FORM = {
  from_id: "",
  to_id: "",
  amount: "",
  date: "",
  notes: "",
};
const FIELD_CLS =
  "w-full rounded-xl border border-palette-gray-divider bg-palette-gray-subtle px-3.5 py-2.5 text-sm text-palette-gray-primary placeholder:text-palette-gray-placeholder focus:border-palette-indigo-ring focus:outline-none focus:ring-2 focus:ring-palette-indigo-border transition-colors";

function Label({ children }) {
  return (
    <label className="mb-1.5 block text-sm font-medium text-palette-gray-heading">{children}</label>
  );
}

function Caret() {
  return (
    <svg
      className="pointer-events-none h-3.5 w-3.5 shrink-0 text-palette-gray-secondary"
      viewBox="0 0 10 6"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M1 1l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function AccountSelect({ label, value, onChange, options, excludeId }) {
  // The select's value is the NETSUITE account id (`bank_account_id`), kept that
  // way so callers seeding `initialValues.from_id`/`to_id` with NetSuite ids
  // (ClinicCashFlow's associate-fee funding flow, and SFC's 1009) still work. The local
  // PK is carried alongside as `id` and is what handleSave actually sends.
  // An account with no NetSuite id is unpickable: it can't be a JE side.
  const available = options.filter(
    (o) => o.bank_name && o.bank_account_id != null && o.bank_account_id !== excludeId,
  );
  const selected = available.find((o) => o.bank_account_id === value) || null;

  return (
    <div>
      <Label>{label}</Label>
      <Listbox value={value} onChange={onChange}>
        <ListboxButton className={`${FIELD_CLS} flex items-center justify-between gap-2 text-left`}>
          {selected ? (
            <span className="min-w-0">
              <span className="block truncate font-semibold text-palette-gray-primary">
                {cleanBankName(selected.bank_name)}
              </span>
              <span className="block truncate text-xs text-palette-gray-secondary">
                {selected.clinic_name || selected.clinic || ""}
              </span>
            </span>
          ) : (
            <span className="text-palette-gray-placeholder">Select an account…</span>
          )}
          <Caret />
        </ListboxButton>
        <ListboxOptions
          anchor="bottom start"
          className="z-[60] mt-1 max-h-72 w-[var(--button-width)] overflow-auto rounded-xl border border-palette-gray-divider bg-white py-1 shadow-lg focus:outline-none"
        >
          {available.map((o) => (
            <ListboxOption
              key={o.bank_account_id}
              value={o.bank_account_id}
              className="cursor-pointer px-3.5 py-2.5 data-[focus]:bg-palette-indigo-soft data-[selected]:bg-palette-indigo-soft"
            >
              <div className="truncate font-semibold text-palette-gray-primary">
                {cleanBankName(o.bank_name)}
              </div>
              <div className="truncate text-xs text-palette-gray-secondary">
                {o.clinic_name || o.clinic || ""}
              </div>
            </ListboxOption>
          ))}
        </ListboxOptions>
      </Listbox>
    </div>
  );
}

export default function TransferModal({
  isOpen,
  onClose,
  onSave,
  clinicOptions = [],
  saving = false,
  initialValues = null,
  maxDate,
  title = "New transfer",
  subtitle = "Intercompany fund move between accounts",
  submitLabel = "Stage for approval",
}) {
  const [form, setForm] = useState(EMPTY_FORM);

  const accountOptions = clinicOptions;

  useEffect(() => {
    if (isOpen) setForm({ ...EMPTY_FORM, ...(initialValues ?? {}) });
  }, [isOpen, initialValues]);

  const set = (key) => (val) => setForm((f) => ({ ...f, [key]: val }));

  const canSave =
    form.from_id !== "" &&
    form.to_id !== "" &&
    form.from_id !== form.to_id &&
    form.amount !== "" &&
    Number(form.amount) > 0 &&
    form.date;

  function handleSave() {
    if (!canSave) return;
    const fromOpt = accountOptions.find((o) => o.bank_account_id === form.from_id);
    const toOpt = accountOptions.find((o) => o.bank_account_id === form.to_id);
    if (!fromOpt || !toOpt) return;
    // Writes reference the local BankAccount PKs. The backend derives the
    // NetSuite ids from them, so the name snapshot and clinic-code inputs the old
    // payload carried are gone — the account is the single source for both.
    onSave?.({
      from_bank_account: fromOpt.id,
      to_bank_account: toOpt.id,
      amount: form.amount,
      date: form.date,
      notes: form.notes.trim(),
    });
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      size="xl"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={!canSave || saving}>
            {saving ? "Saving…" : submitLabel}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <AccountSelect
          label="From account"
          value={form.from_id}
          onChange={set("from_id")}
          options={accountOptions}
          excludeId={form.to_id}
        />
        <AccountSelect
          label="To account"
          value={form.to_id}
          onChange={set("to_id")}
          options={accountOptions}
          excludeId={form.from_id}
        />
        <div>
          <Label>Amount</Label>
          <input
            type="number"
            step="0.01"
            inputMode="decimal"
            value={form.amount}
            onChange={(e) => set("amount")(e.target.value)}
            placeholder="0.00"
            className={FIELD_CLS}
          />
        </div>
        <div>
          <Label>Date</Label>
          <input
            type="date"
            value={form.date}
            max={maxDate}
            onChange={(e) => set("date")(e.target.value)}
            className={FIELD_CLS}
          />
        </div>
        <div>
          <Label>Notes</Label>
          <textarea
            rows={3}
            value={form.notes}
            onChange={(e) => set("notes")(e.target.value)}
            placeholder="Optional"
            className={`${FIELD_CLS} resize-none`}
          />
        </div>
      </div>
    </Modal>
  );
}
